import * as THREE from 'three';
import gsap from 'gsap';
import { computeBoundsTree, disposeBoundsTree, acceleratedRaycast } from 'three-mesh-bvh';
import Experience from '../Experience';

import TerrainGenerator from '../Utils/TerrainGenerator';
import Portal from './Portal';
import VoidEyeAttacks from '../Utils/VoidEyeAttacks';

import { MAP_THEMES } from '../Utils/configFile';

THREE.BufferGeometry.prototype.computeBoundsTree = computeBoundsTree;
THREE.BufferGeometry.prototype.disposeBoundsTree = disposeBoundsTree;
THREE.Mesh.prototype.raycast = acceleratedRaycast;

export default class Map
{
    constructor()
    {
        this.experience = new Experience();
        this.scene = this.experience.scene;
        this.resources = this.experience.resources;
        this.debug = this.experience.debug;
        this.camera = this.experience.camera.instance;

        if (this.debug.active)
        {
            this.debugFolder = this.debug.ui.addFolder({ title: 'Map', expanded: false });
        }

        this.areaTemplates = [];
        this.difficultyLevel = 0;
        this.rowsPassed = 0;
        this.rowsPerDifficulty = 2;

        this.chunkLength = 0;
        this.chunkWidth = 0;
        this.chunks = [];

        this._shipToMapMatrix = new THREE.Matrix4();
        this._inverseShipToMapMatrix = new THREE.Matrix4();
        this._localShipBox = new THREE.Box3();
        this._shipColliderCache = null;

        this.activeFloor = null;
        this.terrainDebugFolder = null;
        this.collisionPoint = null;

        this.portal = new Portal(this.experience, this)
    }


    updateTheme(theme)
    {
        this.clearCurrentEnvironment();

        const config = MAP_THEMES[theme.modelKey];

        if (!config)
        {
            console.warn(`Theme configuration for "${theme.modelKey}" not found!`);
            return;
        }

        this.noOfColumns = config.columns;

        if (config.isGeneratedTerrain)
        {
            const modelScene = this.resources.items[`${theme.modelKey}Model`].scene;
            const terrainData = TerrainGenerator.build(
                modelScene,
                config.terrainAreas,
                config.terrainColors,
                this.debugFolder
            );
            this._applyTerrainData(terrainData);

        } else
        {
            const tempGeo = new THREE.PlaneGeometry(1, 1, 1, 1);
            const tempMat = new THREE.MeshStandardMaterial({ color: 0x222222, roughness: 0.2 });
            const tempMesh = new THREE.Mesh(tempGeo, tempMat);
            tempMesh.position.set(0, 0, 0);
            tempMesh.rotation.x = -Math.PI / 2;
            tempMesh.visible = false;

            this._applyTerrainData({
                areaTemplates: [tempMesh],
                chunkLength: 1382.854263305664,
                chunkWidth: 364.219970703125,
                debugFolder: this.debugFolder
            });
        }

        this.activeFloor = new config.floorClass(
            this.scene,
            this.debug,
            this.resources,
            this.chunkWidth,
            this.chunkLength
        );

        if (config.hasAttacks)
        {
            this.voidEyeAttacks = new VoidEyeAttacks(this.scene, this.chunkWidth, this.chunkLength);
        }

        if (this.experience.world.ship.particlesTrail)
        {
            this.experience.world.ship.particlesTrail.toggleEmission(config.isEmitting);
        }

        this.experience.world.levelManager.setTimerForMap(config.timerKey);
        this.reset();
    }

    _applyTerrainData(data)
    {
        this.areaTemplates = data.areaTemplates;
        this.chunkLength = data.chunkLength;
        this.chunkWidth = data.chunkWidth;
        this.terrainDebugFolder = data.debugFolder;
    }

    clearCurrentEnvironment()
    {
        // Clear Chunks
        for (const chunk of this.chunks)
        {
            this.scene.remove(chunk);
            chunk.traverse(child =>
            {
                if (child.geometry) child.geometry.dispose();
                if (child.material) Array.isArray(child.material) ? child.material.forEach(m => m.dispose()) : child.material.dispose();
            });
        }
        this.chunks = [];

        if (this.voidEyeAttacks)
        {
            this.voidEyeAttacks.destroy();
            this.voidEyeAttacks = null;
        }

        // Clear Templates
        for (const template of this.areaTemplates)
        {
            if (template.geometry) template.geometry.dispose();
            if (template.material) template.material.dispose();
        }
        this.areaTemplates = [];
        this.chunkLength = 0;
        this.chunkWidth = 0;

        // Clear Floor
        if (this.activeFloor)
        {
            this.activeFloor.destroy();
            this.activeFloor = null;
        }

        // Clear Terrain Debug
        if (this.terrainDebugFolder)
        {
            this.terrainDebugFolder.dispose();
            this.terrainDebugFolder = null;
        }
    }

    reset()
    {
        for (const chunk of this.chunks) this.scene.remove(chunk);

        if (this.voidEyeAttacks)
        {
            this.activeFloor.reset();
            this.voidEyeAttacks.reset();
        }

        if (this.experience.world.environment?.currentThemeInstance.name === 'dataStreamTheme' || this.experience.world.environment?.currentThemeInstance.name === 'hushTheme')
        {
            this.experience.world.environment.currentThemeInstance.reset();
        }

        if (this.experience.world.levelManager) this.experience.world.levelManager.reset();

        this.chunks = [];
        this._shipColliderCache = null;
        this.createInfiniteMap();
    }

    createInfiniteMap()
    {
        this.gridColumns = this.noOfColumns || 3;
        this.gridRows = 2;
        this.mapTotalWidth = this.gridColumns * this.chunkWidth;
        this.mapTotalDepth = this.gridRows * this.chunkLength;
        this.initialSpawnOffset = 500;
        const halfCol = Math.floor(this.gridColumns / 2);

        this.difficultyLevel = 0;
        this.rowsPassed = 0;

        for (let x = -halfCol; x <= halfCol; x++)
        {
            for (let z = 0; z < this.gridRows; z++)
            {
                const chunkGroup = new THREE.Group();
                chunkGroup.position.set(x * this.chunkWidth, 0, -(z * this.chunkLength) - this.initialSpawnOffset);

                const areaMesh = this.areaTemplates[this.difficultyLevel].clone();
                chunkGroup.add(areaMesh);

                chunkGroup.userData.colliders = [];
                chunkGroup.traverse(child =>
                {
                    if (child.userData.isCollider) chunkGroup.userData.colliders.push(child);
                });

                this.scene.add(chunkGroup);
                this.chunks.push(chunkGroup);
            }
        }
    }

    update(shipVelocity, forwardSpeed)
    {
        const deltaTime = this.experience.time.delta;

        let effectiveShipVelocity = shipVelocity;

        if (this.gridColumns === 1 && this.chunks.length > 0) 
        {
            const limitX = (this.chunkWidth / 4) * 0.99;
            const currentX = this.chunks[0].position.x;

            const proposedX = currentX - (shipVelocity * deltaTime);

            if (proposedX > limitX) 
            {
                const allowedDelta = currentX - limitX;
                effectiveShipVelocity = allowedDelta / deltaTime;
            }
            else if (proposedX < -limitX) 
            {
                const allowedDelta = currentX - (-limitX);
                effectiveShipVelocity = allowedDelta / deltaTime;
            }
        }


        if (this.portal) this.portal.update(deltaTime);

        if (this.activeFloor) { this.activeFloor.update(deltaTime, effectiveShipVelocity, forwardSpeed); }

        if (this.voidEyeAttacks) { this.voidEyeAttacks.update(deltaTime, effectiveShipVelocity, forwardSpeed); }

        const boundaryX = (this.mapTotalWidth / 2);
        const maxXOffset = this.chunkWidth * 0.9;
        let sharedRowOffset = null;

        for (const chunk of this.chunks)
        {
            chunk.position.z += forwardSpeed * deltaTime;

            chunk.position.x -= effectiveShipVelocity * deltaTime;

            if (chunk.position.z > this.chunkLength)
            {
                chunk.position.z -= this.mapTotalDepth;

                if (sharedRowOffset === null)
                {
                    if (this.gridColumns === 1)
                    {
                        sharedRowOffset = 0;
                    } else
                    {
                        sharedRowOffset = (Math.random() - 0.5) * maxXOffset;
                    }

                    this.rowsPassed++;
                    this.difficultyLevel = Math.min(this.areaTemplates.length - 1, Math.floor(this.rowsPassed / this.rowsPerDifficulty));
                }

                chunk.position.x += sharedRowOffset;
                const templateMesh = this.areaTemplates.length > 1 ? this.areaTemplates[this.difficultyLevel] : this.areaTemplates[0];
                chunk.children[0].geometry = templateMesh.geometry;
            }

            if (chunk.position.x < -boundaryX) chunk.position.x += this.mapTotalWidth;
            else if (chunk.position.x > boundaryX) chunk.position.x -= this.mapTotalWidth;
        }
    }

    checkCollisions(shipGroup)
    {
        if (!shipGroup) return false;

        if (!this._shipColliderCache)
        {
            this._shipColliderCache = shipGroup.children.find(child => /collider/i.test(child.name));
        }

        const shipCollider = this._shipColliderCache;
        if (!shipCollider || !shipCollider.geometry) return false;

        shipCollider.updateMatrixWorld();
        if (!shipCollider.geometry.boundingBox) shipCollider.geometry.computeBoundingBox();

        if (this.voidEyeAttacks)
        {
            const collisionPoint = this.voidEyeAttacks.checkCollisions(shipCollider);
            if (collisionPoint)
            {
                this.collisionPoint = collisionPoint;
                return true;
            }
        }

        if (this.experience.world.environment.currentThemeInstance.name === 'dataStreamTheme')
        {
            const collisionPoint = this.experience.world.environment.currentThemeInstance.checkCollisions(shipCollider);
            if (collisionPoint)
            {
                this.collisionPoint = collisionPoint;
                return true;
            }
        }

        if (this.experience.world.environment.currentThemeInstance.name === 'hushTheme')
        {

            const collisionPoint = this.experience.world.environment.currentThemeInstance.checkCollisions(shipCollider);
            if (collisionPoint)
            {
                this.collisionPoint = collisionPoint;
                return true;
            }
        }


        for (const chunk of this.chunks)
        {
            if (Math.abs(chunk.position.z) > this.chunkLength) continue;
            if (Math.abs(chunk.position.x) > this.chunkWidth) continue;

            const mapColliders = chunk.userData.colliders;

            for (const mapCollider of mapColliders)
            {
                mapCollider.updateMatrixWorld();

                this._shipToMapMatrix.copy(mapCollider.matrixWorld).invert().multiply(shipCollider.matrixWorld);
                this._inverseShipToMapMatrix.copy(this._shipToMapMatrix).invert();

                let collisionPoint = null;

                mapCollider.geometry.boundsTree.shapecast({
                    intersectsBounds: (box) =>
                    {
                        this._localShipBox.copy(shipCollider.geometry.boundingBox).applyMatrix4(this._shipToMapMatrix);
                        return box.intersectsBox(this._localShipBox);
                    },

                    intersectsTriangle: (tri) =>
                    {
                        const hit = shipCollider.geometry.boundsTree.intersectsGeometry(
                            mapCollider.geometry,
                            this._inverseShipToMapMatrix,
                            tri
                        );

                        if (hit)
                        {
                            collisionPoint = tri.a.clone().applyMatrix4(mapCollider.matrixWorld);
                            return true;
                        }
                    }
                });

                if (collisionPoint)
                {
                    this.collisionPoint = collisionPoint;
                    return true;
                }
            }
        }
        return false;
    }

    createPortal()
    {
        this.portalUniforms = {
            uTime: { value: 0 },
            uFrequency: { value: 1.4 },
            uDistortion: { value: 0.01 },
            uNoiseScale: { value: 2.0 },
            uNoiseOffset: { value: 0.1 },
            uBrightness: { value: 0.4 },
            uFacOffset: { value: 0.1 },
            uColor: { value: new THREE.Color(0.961, 0.592, 0.078) },
            uEnterProgress: { value: 0.0 },
            uOpacity: { value: 0.0 }
        };

        const material = new THREE.ShaderMaterial({
            vertexShader: portalVertShader,
            fragmentShader: portalFragShader,
            uniforms: this.portalUniforms,
            transparent: true,
        });

        const geometry = new THREE.PlaneGeometry(10, 10, 64, 64);

        this.portal = new THREE.Mesh(geometry, material);
        this.portal.position.set(0, 5, -5);
        this.portal.scale.set(0, 0, 0);
        this.scene.add(this.portal);

        this.canExitPortal = false;

        window.addEventListener('dblclick', () => this.enterPortal());

        if (this.debug.active) 
        {
            const portalFolder = this.debugFolder.addFolder({ title: 'Portal' });
            portalFolder.addBinding(this.portalUniforms.uFrequency, 'value', { label: 'Frequency', min: 0, max: 10, step: 0.01 });
            portalFolder.addBinding(this.portalUniforms.uDistortion, 'value', { label: 'Distortion', min: 0, max: 0.1, step: 0.001 });
            portalFolder.addBinding(this.portalUniforms.uNoiseScale, 'value', { label: 'Noise Scale', min: 0, max: 10, step: 0.1 });
            portalFolder.addBinding(this.portalUniforms.uNoiseOffset, 'value', { label: 'Noise Offset', min: 0, max: 2, step: 0.01 });
            portalFolder.addBinding(this.portalUniforms.uBrightness, 'value', { label: 'Brightness', min: 0, max: 2, step: 0.01 });
            portalFolder.addBinding(this.portalUniforms.uFacOffset, 'value', { label: 'Factor Offset', min: -2, max: 2, step: 0.01 });
            portalFolder.addBinding(this.portalUniforms.uColor, 'value', { label: 'Color', view: 'color', color: { type: 'float' } });
        }
    }

    enterPortal()
    {
        if (this.canExitPortal) return;

        const tl = gsap.timeline({
            onComplete: () =>
            {
                this.canExitPortal = true;
                setTimeout(() =>
                {
                    this.exitPortal()
                }, 2000);
            }
        });

        tl.to(this.portal.scale, { x: 1, y: 1, z: 1, duration: 0.5, ease: "power2.out" }, 0);
        tl.to(this.portalUniforms.uOpacity, { value: 1.0, duration: 0.5, ease: "power2.out" }, 0);
        tl.fromTo(this.portalUniforms.uFacOffset, { value: 2.0 }, { value: 0.1, duration: 0.1, ease: "power2.out" }, 0);


        this.originalFov = this.camera.fov;

        for (const chunk of this.chunks)
        {
            chunk.traverse((child) =>
            {

                chunk.visible = false;

                if (child.isMesh && child.material)
                {
                    child.material.opacity = 1;
                    child.material.transparent = false;
                }
            });
        }

        if (this.activeFloor)
        {
            if (this.activeFloor.name === 'WaterFloor')
            {
                this.activeFloor.mesh.material.uniforms.uOpacity.value = 0
                this.activeFloor.mesh.material.depthTest = false;

                this.experience.world.environment.currentThemeInstance.planetBackground.visible = false
                this.experience.world.environment.currentThemeInstance.cloudMesh.visible = false
            } else if (this.activeFloor.name === 'SandFloor')
            {
                this.experience.world.environment.currentThemeInstance.planetBackground.visible = false
                this.experience.world.environment.currentThemeInstance.backgroundScreen.visible = false
                this.experience.world.environment.currentThemeInstance.pyramid.visible = false
                this.activeFloor.mesh.visible = false
            }
        }

        tl.to(this.portal.scale, { x: 50, y: 50, z: 50, duration: 2.0, ease: "power2.outIn" }, 0);
        tl.to(this.portal.position, { x: this.portal.position.x, y: this.portal.position.y, z: 100, duration: 1.0, ease: "power2.outIn" }, 0);
        tl.to(this.portalUniforms.uEnterProgress, { value: 1.0, duration: 2.0, ease: "power2.outIn" }, 0);
        tl.to(this.camera, {
            fov: 90, duration: 2.0, ease: "power2.outIn",
            onUpdate: () => this.camera.updateProjectionMatrix()
        }, 0);
        tl.to(this.portalUniforms.uFacOffset, { value: 0.0, duration: 1.2, ease: "power2.outIn" }, 0);
    }

    exitPortal()
    {
        if (!this.canExitPortal) return;

        if (this.experience.world.environment.currentTheme === 'pillar')
        {
            this.experience.world.environment.switchTheme('pyramid');
        } else
        {
            this.experience.world.environment.switchTheme('pillar');
        }

        const tl = gsap.timeline({
            onComplete: () =>
            {
                this.portal.scale.set(0, 0, 0);
                this.portal.position.set(0, 5, -5);
                this.canExitPortal = false;
            }
        });

        tl.to(this.portal.scale, { x: 1, y: 1, z: 1, duration: 2.0, ease: "power2.outIn" }, 0);
        tl.to(this.portal.position, { x: this.portal.position.x, y: this.portal.position.y, z: 0, duration: 2.0, ease: "power2.outIn" }, 0);
        tl.to(this.portalUniforms.uEnterProgress, { value: 0.0, duration: 2.0, ease: "power2.outIn" }, 0);
        tl.to(this.portalUniforms.uFacOffset, { value: -1.0, duration: 2.0, ease: "power2.outIn" }, 0);
        tl.to(this.camera, {
            fov: this.originalFov || 45, duration: 2.0, ease: "power2.outIn",
            onUpdate: () => this.camera.updateProjectionMatrix()
        }, 0);
        tl.to(this.portalUniforms.uOpacity, { value: 0.0, duration: 2.0, ease: "power2.outIn" }, 0);
    }
}